/**
 * GlobalTools Registry
 *
 * Central registry for all global tools. Tools register themselves here
 * and can be looked up by ID or matched against trigger patterns.
 */

import type {
  GlobalToolDefinition,
  GlobalToolId,
  TriggerMatch,
} from './types';
import { mentionsAssistant } from '../threadMentions/summonGate';

/**
 * Strip LEADING assistant mentions ("@Prest0n ", "@preston", the composer's
 * `<@assistant>` token) before trigger matching (2026-08-11).
 *
 * Every command-style tool trigger is anchored to the start of the message
 * ("^connect my …", "^translate …"), but in a mention-gated group thread the
 * same command is necessarily written "@Prest0n connect my gmail" — the render
 * layer even ADDS that prefix to suggestion chips (suggestionMention.ts). The
 * mention is addressing, not content: matching against the stripped text lets
 * the one-click connect card (and every other anchored tool) fire identically
 * in 1:1 and group threads. Only assistant handles are stripped, only from the
 * front, and only when the mention gate would recognize them — a leading
 * mention of a human teammate leaves the text untouched (that message is for
 * them, not for a tool).
 */
const LEADING_ASSISTANT_MENTION =
  /^(?:@(?:prest0n|preston|prest0n-ai|preston-ai)\b[,:]?\s*|<@assistant>\s*)+/i;

export function stripLeadingAssistantMention(text: string): string {
  const trimmed = (text || '').trim();
  if (!trimmed.startsWith('@') && !trimmed.startsWith('<@')) return trimmed;
  const stripped = trimmed.replace(LEADING_ASSISTANT_MENTION, '').trim();
  // Only accept the strip when the removed prefix really addressed the
  // assistant (defense against the regex and summonGate's handle set ever
  // drifting apart — mentionsAssistant is the canonical predicate).
  if (stripped !== trimmed && !mentionsAssistant(trimmed)) return trimmed;
  return stripped;
}

// ============================================================================
// Tool Registry Storage
// ============================================================================

/**
 * Internal registry map of tool ID to tool definition.
 */
const toolRegistry = new Map<GlobalToolId, GlobalToolDefinition<unknown>>();

// ============================================================================
// Registration Functions
// ============================================================================

/**
 * Register a tool in the global registry.
 * @param tool - The tool definition to register
 * @throws Error if a tool with the same ID is already registered
 */
export function registerTool<TState>(tool: GlobalToolDefinition<TState>): void {
  // Defensive guard: a circular import can cause a tool binding to be
  // `undefined` at the moment registerAllTools() runs (module-init order).
  // Without this guard, `tool.id` throws a TypeError that aborts the rest of
  // registerAllTools(), silently leaving later tools unregistered. Guarding
  // here keeps registration resilient so one bad binding can't drop the rest.
  if (!tool || !tool.id) {
    console.warn(
      '[GlobalTools Registry] Attempted to register an undefined/invalid tool (missing id) — skipping. This usually indicates a circular import or module-init ordering problem.'
    );
    return;
  }
  if (toolRegistry.has(tool.id)) {
    console.warn(
      `[GlobalTools Registry] Tool "${tool.id}" is already registered. Replacing.`
    );
  }
  toolRegistry.set(tool.id, tool as GlobalToolDefinition<unknown>);
}

/**
 * Unregister a tool from the registry.
 * @param toolId - The ID of the tool to unregister
 * @returns true if the tool was found and removed, false otherwise
 */
export function unregisterTool(toolId: GlobalToolId): boolean {
  return toolRegistry.delete(toolId);
}

/**
 * Clear all registered tools.
 * Primarily used for testing.
 */
export function clearRegistry(): void {
  toolRegistry.clear();
}

// ============================================================================
// Lookup Functions
// ============================================================================

/**
 * Get a tool by its ID.
 * @param toolId - The ID of the tool to retrieve
 * @returns The tool definition or undefined if not found
 */
export function getToolById<TState = unknown>(
  toolId: GlobalToolId
): GlobalToolDefinition<TState> | undefined {
  return toolRegistry.get(toolId) as GlobalToolDefinition<TState> | undefined;
}

/**
 * Get all registered tools.
 * @returns Array of all registered tool definitions
 */
export function getAllRegisteredTools(): GlobalToolDefinition<unknown>[] {
  return Array.from(toolRegistry.values());
}

/**
 * Get the IDs of all registered tools.
 * @returns Array of all registered tool IDs
 */
export function getRegisteredToolIds(): GlobalToolId[] {
  return Array.from(toolRegistry.keys());
}

/**
 * Check if a tool is registered.
 * @param toolId - The ID to check
 * @returns true if the tool is registered
 */
export function isToolRegistered(toolId: GlobalToolId): boolean {
  return toolRegistry.has(toolId);
}

// ============================================================================
// Trigger Pattern Matching
// ============================================================================

/**
 * Match a message text against all registered tool trigger patterns.
 * Returns the first matching tool and match details.
 *
 * @param text - The message text to match against
 * @returns Object with matching tool and match details, or null if no match
 */
export function matchToolTrigger(
  text: string
): { tool: GlobalToolDefinition<unknown>; match: TriggerMatch } | null {
  // "@Prest0n connect my gmail" must trigger exactly like "connect my gmail":
  // anchored command triggers see the text with any leading assistant mention
  // removed (see stripLeadingAssistantMention).
  const trimmedText = stripLeadingAssistantMention(text);

  for (const tool of toolRegistry.values()) {
    const match = evaluateTriggerPattern(tool.triggerPattern, trimmedText);
    if (match.matches) {
      return { tool, match };
    }
  }

  return null;
}

/**
 * Match a message text against a specific tool's trigger pattern.
 *
 * @param toolId - The ID of the tool to check
 * @param text - The message text to match against
 * @returns TriggerMatch object with match details
 */
export function matchToolTriggerById(toolId: GlobalToolId, text: string): TriggerMatch {
  const tool = toolRegistry.get(toolId);
  if (!tool) {
    return { matches: false };
  }

  // Same leading-mention normalization as matchToolTrigger, so by-id rechecks
  // agree with the registry sweep about what a message triggers.
  return evaluateTriggerPattern(tool.triggerPattern, stripLeadingAssistantMention(text));
}

/**
 * Evaluate a trigger pattern against text.
 * Supports both RegExp and function patterns.
 */
function evaluateTriggerPattern(
  pattern: GlobalToolDefinition<unknown>['triggerPattern'],
  text: string
): TriggerMatch {
  if (typeof pattern === 'function') {
    return pattern(text);
  }

  // RegExp pattern
  const match = text.match(pattern);
  if (!match) {
    return { matches: false };
  }

  // Extract query from text after the matched pattern
  const query = text.slice(match[0].length).trim() || undefined;

  return {
    matches: true,
    query,
  };
}

// ============================================================================
// Tool Filtering
// ============================================================================

/**
 * Get tools that can be triggered from buttons (not just chat).
 * @returns Array of tools with canTriggerFromButton set to true
 */
export function getButtonTriggerableTools(): GlobalToolDefinition<unknown>[] {
  return Array.from(toolRegistry.values()).filter(
    (tool) => tool.canTriggerFromButton === true
  );
}

/**
 * Get tools that a specific user is authorized to use.
 * @param userEmail - The user's email address
 * @returns Array of tools the user can access
 */
export function getAuthorizedTools(
  userEmail?: string
): GlobalToolDefinition<unknown>[] {
  return Array.from(toolRegistry.values()).filter((tool) => {
    // If no authorization check, tool is available to all
    if (!tool.isAuthorized) return true;
    // Check if user is authorized
    return tool.isAuthorized(userEmail);
  });
}

// ============================================================================
// Button ID Mapping
// ============================================================================

/**
 * Find a tool by one of its button IDs.
 * Useful for event delegation when a button is clicked.
 *
 * @param buttonId - The button ID to search for
 * @returns Object with tool and button key, or null if not found
 */
export function findToolByButtonId(
  buttonId: string
): { tool: GlobalToolDefinition<unknown>; buttonKey: string } | null {
  for (const tool of toolRegistry.values()) {
    for (const [key, id] of Object.entries(tool.buttonIds)) {
      // Check for exact match or prefix match (for dynamic IDs like "btn-123")
      if (id === buttonId || buttonId.startsWith(id)) {
        return { tool, buttonKey: key };
      }
    }
  }
  return null;
}

// ============================================================================
// Registry Info
// ============================================================================

/**
 * Get registry statistics.
 * @returns Object with registry stats
 */
export function getRegistryStats(): {
  totalTools: number;
  toolIds: GlobalToolId[];
  buttonMappings: Record<GlobalToolId, Record<string, string>>;
} {
  const toolIds = Array.from(toolRegistry.keys());
  const buttonMappings: Record<GlobalToolId, Record<string, string>> = {} as Record<
    GlobalToolId,
    Record<string, string>
  >;

  for (const [id, tool] of toolRegistry.entries()) {
    buttonMappings[id] = tool.buttonIds;
  }

  return {
    totalTools: toolRegistry.size,
    toolIds,
    buttonMappings,
  };
}
